/**
 * Billing & Pricing Constants
 * Centralizes all pricing, fees, and billing configurations
 */

// Commission rates
export const COMMISSION_RATES = {
  DEFAULT: 0.10,     // 10% commission
  PARTNER: 0.08,     // 8% for partners
  PREMIUM: 0.05,     // 5% for premium partners
  BASIC: 0.15,       // 15% for basic plan
  PRO: 0.12,         // 12% for pro plan
  FREE: 0.20,        // 20% for free plan
} as const;

// Coupon codes
export const COUPON_CODES = {
  WELCOME: 'HOLA-ALLTUC',
  CLUB_20: 'CLUB20',
  CLUB_10: 'CLUB10',
  FREE_SHIPPING: 'ENVIO-GRATIS',
  TUCUMAN_2026: 'TUCUMAN2026',
  ENVIO_YA: 'ENVIOYA',
  VALID_CODES: [
    'TUCUMAN2026',
    'ENVIOYA',
    'CLUB20',
    'CLUB10',
    'HOLA-ALLTUC',
    'ENVIO-GRATIS',
  ],
} as const;

// Price limits
export const PRICE_LIMITS = {
  FILTER_MIN: 0,
  FILTER_MAX: 100000,
  CART_UPSELL_THRESHOLD: 10000,
  TOPUP_OPTIONS: [1000, 2000, 5000, 10000] as const,
  CLUB_VOUCHER_5000: 5000,
  TRANSFER_DISCOUNT_THRESHOLD: 40000,
  FREE_SHIPPING_DISPLAY_THRESHOLD: 20000,
  INSTALLMENT_THRESHOLD: 20000,
  IMPULSE_THRESHOLD: 20000,
} as const;

// Main billing config
export const BILLING = {
  COMMISSION: {
    DEFAULT_RATE: COMMISSION_RATES.DEFAULT,
    PARTNER_RATE: COMMISSION_RATES.PARTNER,
    PREMIUM_RATE: COMMISSION_RATES.PREMIUM,
  },
  CLUB: {
    CLUB_20_DISCOUNT_RATE: 0.20, // 20% discount
    CLUB_10_DISCOUNT_RATE: 0.10, // 10% discount
    CLUB_PRICE_MULTIPLIER: 0.9, // Club members get 10% off
  },
  COUPONS: {
    WELCOME_MAX_DISCOUNT: 5000,
    VALID_CODES: COUPON_CODES.VALID_CODES,
    DISCOUNT_RATES: {
      'HOLA-ALLTUC': { type: 'flat', max: 5000 },
      'CLUB20': { type: 'percentage', value: 0.20 },
      'CLUB10': { type: 'percentage', value: 0.10 },
    },
  },
  MEMBERSHIP: {
    CLUB_VOUCHER_5000: PRICE_LIMITS.CLUB_VOUCHER_5000,
  },
  WALLET: {
    DEFAULT_BALANCE: 0,
    MIN_TOPUP: 1000,
    MAX_TOPUP: 100000,
    TOPUP_OPTIONS: PRICE_LIMITS.TOPUP_OPTIONS,
  },
  SHIPPING: {
    LOCAL_COST: 1500,
    FREE_THRESHOLD: 50000,
    SAME_DAY_CUTOFF_HOUR: 14, // 2 PM
  },
} as const;

/**
 * Get coupon discount
 */
export function getCouponDiscount(code: string, cartTotal: number): number {
  const upperCode = code.toUpperCase();
  const validCodes = BILLING.COUPONS.VALID_CODES;

  if (!validCodes.includes(upperCode)) return 0;

  // Manually handle each code to avoid type errors
  if (upperCode === 'HOLA-ALLTUC') {
    return Math.min(BILLING.COUPONS.WELCOME_MAX_DISCOUNT, cartTotal);
  }

  if (upperCode === 'CLUB20') {
    return Math.round(cartTotal * BILLING.CLUB.CLUB_20_DISCOUNT_RATE);
  }

  if (upperCode === 'CLUB10') {
    return Math.round(cartTotal * BILLING.CLUB.CLUB_10_DISCOUNT_RATE);
  }

  return 0;
}

/**
 * Check if coupon is valid
 */
export function isValidCoupon(code: string): boolean {
  return COUPON_CODES.VALID_CODES.includes(code.toUpperCase() as typeof COUPON_CODES.VALID_CODES[number]);
}
